// Team name → logo abbreviation, per league.
//
// The Remotion composition builds each emblem URL straight from the `sports`
// prop and the team abbreviation, so `team1`/`team2` must carry abbreviations
// that exist under public/logos/{league}/. The prompt asks the model for
// abbreviations directly, but models drift — returning a full name
// ("Kansas City Chiefs"), a nickname ("Chiefs"), or a rival abbreviation
// convention ("WSH" where the folder holds "WAS"). Everything here exists to
// land those on a file that is actually present.
//
// Verified live against https://sportsgrid-labs.sportsgrid.com/logos/:
//   nfl  — JAC and WSH are NOT uploaded (only JAX, WAS)
//   nhl  — LAK, NJD, SJS, TBL are NOT uploaded (only LA, NJ, SJ, TB)
// so those forms are remapped rather than passed through.

import type { SportKey } from '@/lib/sports';

const norm = (s: string): string =>
  (s || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.'\u2019]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

// --- Abbreviations that exist as files, per league -------------------------
// Aliases are deliberate: the same logo is uploaded under several abbreviations
// so whichever convention the model uses still resolves.

const NFL_ABBRS = [
  'ARI','ATL','BAL','BUF','CAR','CHI','CIN','CLE','DAL','DEN','DET','GB','HOU',
  'IND','JAX','KC','LA','LAC','LAR','LV','MIA','MIN','NE','NO','NYG','NYJ','PHI',
  'PIT','SEA','SF','TB','TEN','WAS',
];
const NBA_ABBRS = [
  'ATL','BKN','BOS','CHA','CHI','CLE','DAL','DEN','DET','GS','GSW','HOU','IND',
  'LAC','LAK','LAL','MEM','MIA','MIL','MIN','NO','NOP','NY','NYK','OKC','ORL',
  'PHI','PHO','PHX','POR','SA','SAS','SAC','TOR','UTA','UTAH','WAS','WSH',
];
const NHL_ABBRS = [
  'ANA','ARI','BOS','BUF','CAR','CBJ','CGY','CHI','COL','DAL','DET','EDM','FLA',
  'LA','MIN','MON','MTL','NAS','NSH','NJ','NYI','NYR','OTT','PHI','PIT','SEA',
  'SJ','STL','TB','TOR','UTA','VAN','VEG','VGK','WAS','WSH','WPG',
];
const WNBA_ABBRS = [
  'ATL','CHI','CON','CONN','DAL','GSV','IND','LA','LAS','LV','LVA','MIN','NY',
  'NYL','PHO','PHX','POR','SEA','TOR','WAS','WSH',
];

export const LIVE_ABBRS: Record<SportKey, string[]> = {
  nfl: NFL_ABBRS,
  nba: NBA_ABBRS,
  nhl: NHL_ABBRS,
  wnba: WNBA_ABBRS,
  cfb: [], // hundreds of schools; resolved from the Sportradar schedule alias instead
};

// Abbreviations a model plausibly emits that have no file — mapped to one that does.
const ABBR_FIXUPS: Record<SportKey, Record<string, string>> = {
  nfl: { JAC: 'JAX', JAG: 'JAX', WSH: 'WAS', WFT: 'WAS', ARZ: 'ARI', BLT: 'BAL',
         CLV: 'CLE', HST: 'HOU', LVR: 'LV', OAK: 'LV', SD: 'LAC', STL: 'LAR',
         KAN: 'KC', GNB: 'GB', NWE: 'NE', NOR: 'NO', SFO: 'SF', TAM: 'TB', TEN2: 'TEN' },
  nba: { BRK: 'BKN', NJN: 'BKN', CHO: 'CHA', PHO2: 'PHX', GSV: 'GSW', SAN: 'SAS' },
  nhl: { LAK: 'LA', NJD: 'NJ', SJS: 'SJ', TBL: 'TB', CBS: 'CBJ', WSH2: 'WSH',
         VGS: 'VGK', ANH: 'ANA', CLS: 'CBJ', MTL2: 'MTL', PHX: 'ARI' },
  wnba: { GS: 'GSV', GSW: 'GSV', CT: 'CONN', LVA2: 'LV', WSH2: 'WAS', PHI: 'PHO' },
  // College football: Sportradar's alias and our logo filename disagree for a
  // number of schools. Only VERIFIED pairs belong here — guessing is actively
  // harmful, because a wrong guess silently renders another school's logo
  // (e.g. Idaho State is NOT Idaho's "IDA" file). Add a row once confirmed.
  cfb: {
    IDHO: 'IDA',   // Idaho Vandals
    CP: 'CPS',     // Cal Poly Mustangs
    COLG: 'CGT',   // Colgate Raiders
    TNTC: 'TNT',   // Tennessee Tech Golden Eagles
    AAMU: 'AAM',   // Alabama A&M Bulldogs
    COOK: 'BCU',   // Bethune-Cookman Wildcats
    EIU: 'EIL',    // Eastern Illinois Panthers
    ETAM: 'TAMC',  // East Texas A&M Lions
    IDST: 'IDS',   // Idaho State Bengals — NOT Idaho's "IDA"
    MCNS: 'MCN',   // McNeese Cowboys
    MORG: 'MGN',   // Morgan State Bears
    MURR: 'MUR',   // Murray State Racers
    NICH: 'NIC',   // Nicholls State Colonels
    NWST: 'NWS',   // Northwestern State Demons
    PEAY: 'APY',   // Austin Peay Governors
    RICH: 'RCH',   // Richmond Spiders
    SEMO: 'SEM',   // Southeast Missouri State Redhawks
    // Variants the MODEL emits rather than Sportradar aliases. A CFB run is
    // aligned to the picked game's schedule aliases first (see alignToGame in
    // api/gamerecap/run), so these only matter if that alignment cannot match
    // the names. Each is unambiguous in college football.
    APSU: 'APY',   // Austin Peay State University
    GWU: 'WEBB',   // Gardner-Webb (George Washington has no football program)
    NOVA: 'VILL',  // Villanova
  },
};

// --- Full name / nickname → abbreviation ----------------------------------

const NFL_TEAMS: Record<string, string> = {
  arizonacardinals: 'ARI', cardinals: 'ARI',
  atlantafalcons: 'ATL', falcons: 'ATL',
  baltimoreravens: 'BAL', ravens: 'BAL',
  buffalobills: 'BUF', bills: 'BUF',
  carolinapanthers: 'CAR', panthers: 'CAR',
  chicagobears: 'CHI', bears: 'CHI',
  cincinnatibengals: 'CIN', bengals: 'CIN',
  clevelandbrowns: 'CLE', browns: 'CLE',
  dallascowboys: 'DAL', cowboys: 'DAL',
  denverbroncos: 'DEN', broncos: 'DEN',
  detroitlions: 'DET', lions: 'DET',
  greenbaypackers: 'GB', packers: 'GB',
  houstontexans: 'HOU', texans: 'HOU',
  indianapoliscolts: 'IND', colts: 'IND',
  jacksonvillejaguars: 'JAX', jaguars: 'JAX',
  kansascitychiefs: 'KC', chiefs: 'KC',
  losangeleschargers: 'LAC', chargers: 'LAC',
  losangelesrams: 'LAR', rams: 'LAR',
  lasvegasraiders: 'LV', raiders: 'LV',
  miamidolphins: 'MIA', dolphins: 'MIA',
  minnesotavikings: 'MIN', vikings: 'MIN',
  newenglandpatriots: 'NE', patriots: 'NE',
  neworleanssaints: 'NO', saints: 'NO',
  newyorkgiants: 'NYG', giants: 'NYG',
  newyorkjets: 'NYJ', jets: 'NYJ',
  philadelphiaeagles: 'PHI', eagles: 'PHI',
  pittsburghsteelers: 'PIT', steelers: 'PIT',
  seattleseahawks: 'SEA', seahawks: 'SEA',
  sanfrancisco49ers: 'SF', fortyniners: 'SF', niners: 'SF',
  tampabaybuccaneers: 'TB', buccaneers: 'TB', bucs: 'TB',
  tennesseetitans: 'TEN', titans: 'TEN',
  washingtoncommanders: 'WAS', commanders: 'WAS',
};

const NBA_TEAMS: Record<string, string> = {
  atlantahawks: 'ATL', hawks: 'ATL',
  brooklynnets: 'BKN', nets: 'BKN',
  bostonceltics: 'BOS', celtics: 'BOS',
  charlottehornets: 'CHA', hornets: 'CHA',
  chicagobulls: 'CHI', bulls: 'CHI',
  clevelandcavaliers: 'CLE', cavaliers: 'CLE', cavs: 'CLE',
  dallasmavericks: 'DAL', mavericks: 'DAL', mavs: 'DAL',
  denvernuggets: 'DEN', nuggets: 'DEN',
  detroitpistons: 'DET', pistons: 'DET',
  goldenstatewarriors: 'GSW', warriors: 'GSW',
  houstonrockets: 'HOU', rockets: 'HOU',
  indianapacers: 'IND', pacers: 'IND',
  losangelesclippers: 'LAC', clippers: 'LAC',
  losangeleslakers: 'LAL', lakers: 'LAL',
  memphisgrizzlies: 'MEM', grizzlies: 'MEM',
  miamiheat: 'MIA', heat: 'MIA',
  milwaukeebucks: 'MIL', bucks: 'MIL',
  minnesotatimberwolves: 'MIN', timberwolves: 'MIN', wolves: 'MIN',
  neworleanspelicans: 'NOP', pelicans: 'NOP',
  newyorkknicks: 'NYK', knicks: 'NYK',
  oklahomacitythunder: 'OKC', thunder: 'OKC',
  orlandomagic: 'ORL', magic: 'ORL',
  philadelphia76ers: 'PHI', sixers: 'PHI', seventysixers: 'PHI',
  phoenixsuns: 'PHX', suns: 'PHX',
  portlandtrailblazers: 'POR', trailblazers: 'POR', blazers: 'POR',
  sacramentokings: 'SAC', kings: 'SAC',
  sanantoniospurs: 'SAS', spurs: 'SAS',
  torontoraptors: 'TOR', raptors: 'TOR',
  utahjazz: 'UTA', jazz: 'UTA',
  washingtonwizards: 'WAS', wizards: 'WAS',
};

const NHL_TEAMS: Record<string, string> = {
  anaheimducks: 'ANA', ducks: 'ANA',
  utahmammoth: 'UTA', utahhockeyclub: 'UTA', mammoth: 'UTA',
  bostonbruins: 'BOS', bruins: 'BOS',
  buffalosabres: 'BUF', sabres: 'BUF',
  carolinahurricanes: 'CAR', hurricanes: 'CAR', canes: 'CAR',
  columbusbluejackets: 'CBJ', bluejackets: 'CBJ',
  calgaryflames: 'CGY', flames: 'CGY',
  chicagoblackhawks: 'CHI', blackhawks: 'CHI',
  coloradoavalanche: 'COL', avalanche: 'COL', avs: 'COL',
  dallasstars: 'DAL', stars: 'DAL',
  detroitredwings: 'DET', redwings: 'DET',
  edmontonoilers: 'EDM', oilers: 'EDM',
  floridapanthers: 'FLA',
  losangeleskings: 'LA',
  minnesotawild: 'MIN', wild: 'MIN',
  montrealcanadiens: 'MTL', canadiens: 'MTL', habs: 'MTL',
  nashvillepredators: 'NSH', predators: 'NSH', preds: 'NSH',
  newjerseydevils: 'NJ', devils: 'NJ',
  newyorkislanders: 'NYI', islanders: 'NYI',
  newyorkrangers: 'NYR', rangers: 'NYR',
  ottawasenators: 'OTT', senators: 'OTT', sens: 'OTT',
  philadelphiaflyers: 'PHI', flyers: 'PHI',
  pittsburghpenguins: 'PIT', penguins: 'PIT', pens: 'PIT',
  seattlekraken: 'SEA', kraken: 'SEA',
  sanjosesharks: 'SJ', sharks: 'SJ',
  stlouisblues: 'STL', blues: 'STL',
  tampabaylightning: 'TB', lightning: 'TB', bolts: 'TB',
  torontomapleleafs: 'TOR', mapleleafs: 'TOR', leafs: 'TOR',
  vancouvercanucks: 'VAN', canucks: 'VAN',
  vegasgoldenknights: 'VGK', goldenknights: 'VGK',
  washingtoncapitals: 'WAS', capitals: 'WAS', caps: 'WAS',
  winnipegjets: 'WPG',
};

const WNBA_TEAMS: Record<string, string> = {
  atlantadream: 'ATL', dream: 'ATL',
  chicagosky: 'CHI', sky: 'CHI',
  connecticutsun: 'CONN', sun: 'CONN',
  dallaswings: 'DAL', wings: 'DAL',
  goldenstatevalkyries: 'GSV', valkyries: 'GSV',
  indianafever: 'IND', fever: 'IND',
  lasvegasaces: 'LV', aces: 'LV',
  losangelessparks: 'LA', sparks: 'LA',
  minnesotalynx: 'MIN', lynx: 'MIN',
  newyorkliberty: 'NY', liberty: 'NY',
  phoenixmercury: 'PHX', mercury: 'PHX',
  portlandfire: 'POR', fire: 'POR',
  seattlestorm: 'SEA', storm: 'SEA',
  torontotempo: 'TOR', tempo: 'TOR',
  washingtonmystics: 'WAS', mystics: 'WAS',
};

const TEAMS_BY_SPORT: Record<SportKey, Record<string, string>> = {
  nfl: NFL_TEAMS,
  nba: NBA_TEAMS,
  nhl: NHL_TEAMS,
  wnba: WNBA_TEAMS,
  cfb: {},
};

// --- Shared aliases (CFB only) ---------------------------------------------
//
// Sportradar reuses a handful of aliases for two different schools, and the
// logo filename IS the alias — so one file has to serve both, and the school it
// does not depict renders the *wrong* emblem. That is the only place in this
// pipeline where a wrong logo can appear rather than none, so it is resolved
// from the school name instead of the abbreviation.
//
// Each entry lists only the school(s) the file on disk does NOT depict; the one
// it does keeps the alias untouched. The file each alias actually holds was
// confirmed by opening it (SVG <title>, or rendering it):
//   CSU = Colorado State   DSU = Delta State        KSU = Kansas State
//   WSU = Washington State WEB = Webber Int'l       VSU = Valdosta State
//   LC  = Lane
// `abbr` is the filename the displaced school should use. WBS is already on
// disk (Weber State); the rest have no artwork yet, so they 404 and degrade to
// no emblem — the same graceful path as any other school we have no logo for,
// and strictly better than showing a different school's mark.
// `nameHas` is the one word that separates the schools sharing that alias, not
// their full name, so an abbreviated feed ("Delaware St.") still matches. Each
// is checked only against names that already carry its alias, which is what
// keeps a fragment this short unambiguous — "virginia" would otherwise also
// catch West Virginia State, but that school's alias is WVSU, not VSU.
const CFB_SHARED_ALIASES: Record<string, { nameHas: string; abbr: string }[]> = {
  CSU: [
    { nameHas: 'central', abbr: 'CENTST' },   // Central State (OH) Marauders
    { nameHas: 'chadron', abbr: 'CHADST' },   // Chadron State Eagles
  ],                                          // …leaving Colorado State on CSU
  DSU: [{ nameHas: 'delaware', abbr: 'DELST' }],   // Delaware State Hornets
  KSU: [{ nameHas: 'kentucky', abbr: 'KYST' }],    // Kentucky State Thorobreds
  WSU: [{ nameHas: 'winona', abbr: 'WINST' }],     // Winona State Warriors
  WEB: [{ nameHas: 'weber', abbr: 'WBS' }],        // Weber State — WBS.svg is on disk
  LC: [{ nameHas: 'louisiana', abbr: 'LACHR' }],   // Louisiana Christian Wildcats
  VSU: [{ nameHas: 'virginia', abbr: 'VAST' }],    // Virginia State Trojans
};

/**
 * The abbreviation a shared CFB alias should use for this particular school, or
 * null when the alias is unshared or the name identifies the school the file on
 * disk already depicts.
 */
function disambiguate(sport: SportKey, upper: string, teamName?: string): string | null {
  if (sport !== 'cfb' || !teamName) return null;
  const candidates = CFB_SHARED_ALIASES[upper];
  if (!candidates) return null;
  const n = norm(teamName);
  return candidates.find((c) => n.includes(c.nameHas))?.abbr ?? null;
}

/**
 * Resolve whatever the model (or a schedule feed) called a team into an
 * abbreviation that has a logo file. Returns the input uppercased if nothing
 * matches, so an unmapped school still renders its name — the composition
 * simply drops an emblem whose URL 404s.
 *
 * `teamName` is optional and only consulted for CFB, where a few Sportradar
 * aliases are shared by two schools (see CFB_SHARED_ALIASES). Pass it wherever
 * the school name is known — the schedule feed always does.
 */
export function resolveAbbr(sport: SportKey, raw: string, teamName?: string): string {
  const input = (raw || '').trim();
  if (!input) return '';

  const live = LIVE_ABBRS[sport];
  const fixups = ABBR_FIXUPS[sport] ?? {};
  const upper = input.toUpperCase();

  // An alias two schools share is a valid filename for exactly one of them, so
  // this has to run before the passthrough below would accept it.
  const shared = disambiguate(sport, upper, teamName);
  if (shared) return shared;

  // Already a real file.
  if (live.includes(upper)) return upper;
  // A known-wrong abbreviation with a known-right counterpart.
  if (fixups[upper]) return fixups[upper];
  // A full name or nickname.
  const byName = TEAMS_BY_SPORT[sport][norm(input)];
  if (byName) return byName;
  // Last chance: a name whose final word is a nickname ("the Chiefs").
  const words = norm(input);
  for (const [key, abbr] of Object.entries(TEAMS_BY_SPORT[sport])) {
    if (words.endsWith(key) || words.startsWith(key)) return abbr;
  }
  return upper;
}

/** True when the abbreviation is known to have a logo file uploaded. */
export function hasLogo(sport: SportKey, abbr: string): boolean {
  if (sport === 'cfb') return true; // resolved per-game from Sportradar; assume present
  return LIVE_ABBRS[sport].includes((abbr || '').toUpperCase());
}
